import {
  ATTRIBUTION_HANDOFF_PARAM,
  type AttributionContext,
  buildHandoffUrl,
  CLICK_ID_PARAM_TO_TYPE,
  consumeHandoffIntoContext,
  serializeHandoffParam,
} from "@nextday-ai/common-ts-model";

import { getAttributionHost } from "@attribution/host";
import { readAttributionContext, writeAttributionContext } from "@attribution/storage";

/**
 * Produces the outbound URL with the current envelope encoded into
 * `?ad_h=<blob>`. Returns `undefined` when no envelope has been captured yet,
 * so the caller can fall through to a plain redirect.
 */
export const buildOutboundHandoffUrl = async (targetUrl: string): Promise<string | undefined> => {
  const envelope = readAttributionContext();
  if (envelope === undefined) return undefined;

  const host = getAttributionHost();
  const { encoded } = await serializeHandoffParam(envelope, host.signer);
  return buildHandoffUrl(targetUrl, envelope, encoded);
};

export type ConsumeInboundHandoffResult = {
  handoffAnonymousId?: string;
};

/**
 * Consumes an inbound `?ad_h=` parameter: verifies the signature, merges the
 * handoff envelope into local state per pure-core rules, and persists the
 * result. No-op when the current URL has no handoff.
 */
export const consumeInboundHandoff = async (): Promise<ConsumeInboundHandoffResult | undefined> => {
  if (typeof window === "undefined") return undefined;

  const encoded = new URLSearchParams(window.location.search).get(ATTRIBUTION_HANDOFF_PARAM);
  if (encoded === null || encoded === "") return undefined;

  const host = getAttributionHost();
  const previous = readAttributionContext();
  const result = await consumeHandoffIntoContext(previous, encoded, { verifier: host.verifier });
  if (result === undefined) return undefined;

  writeAttributionContext(result.next);
  return { handoffAnonymousId: result.handoffAnonymousId };
};

/**
 * Strips `ad_h` and every recognized click-id param from the current URL via
 * `history.replaceState`. Run after inbound pixels have had a chance to read
 * the landing URL so the envelope persists but the address bar stays clean.
 */
export const stripAttributionParamsFromUrl = (): void => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  const targets = [ATTRIBUTION_HANDOFF_PARAM, ...Object.keys(CLICK_ID_PARAM_TO_TYPE)];
  let dirty = false;
  for (const name of targets) {
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name);
      dirty = true;
    }
  }
  if (dirty) {
    window.history.replaceState(window.history.state, "", url.toString());
  }
};

export type { AttributionContext };
